import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import api from "../../utils/api";

export type MistakeKind = "internal" | "external";
export type MistakeRole = "designer" | "qc2" | "crm";
export type RedesignScope = "Minor" | "Major" | "New Design";

export type MistakeEvent = {
  live_case_id: string;
  redesign_case_id: string;
  source_case_id: string;
  kind: MistakeKind;
  severity: RedesignScope | "";
  categories: string[];
  note: string;
  created_at: string;
  designer: string[];
  qc2: string;
  crm: string;
};

export type MistakePerson = {
  name: string;
  total: number;
  as_designer: number;
  as_qc2: number;
  as_crm: number;
  internal: number;
  external: number;
  minor: number;
  major: number;
  new_design: number;
  by_category: Record<string, number>;
};

export type MistakeCategoryRow = {
  category: string;
  events: number;
  internal: number;
  external: number;
};

export type MistakeQuery = {
  fromDate?: string;
  toDate?: string;
  kind?: string;
  category?: string;
  severity?: string;
  empName?: string;
  role?: string;
  search?: string;
  page?: number;
  limit?: number;
};

type MistakeState = {
  fromDate: string;
  toDate: string;
  summary: {
    events: number;
    internal: number;
    external: number;
    people: number;
    minor: number;
    major: number;
    new_design: number;
  };
  categoryOptions: string[];
  peopleOptions: string[];
  categories: MistakeCategoryRow[];
  people: MistakePerson[];
  events: MistakeEvent[];
  page: number;
  totalPages: number;
  total: number;
  loading: boolean;
  error: string | null;
};

const emptySummary = {
  events: 0,
  internal: 0,
  external: 0,
  people: 0,
  minor: 0,
  major: 0,
  new_design: 0,
};

const initialState: MistakeState = {
  fromDate: "",
  toDate: "",
  summary: emptySummary,
  categoryOptions: [],
  peopleOptions: [],
  categories: [],
  people: [],
  events: [],
  page: 1,
  totalPages: 0,
  total: 0,
  loading: false,
  error: null,
};

export const updateRedesignMistakeCategory = createAsyncThunk(
  "mistakeTracker/updateCategory",
  async (
    body: {
      kind: MistakeKind;
      redesign_case_id: string;
      categories: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await api.put("/units/mistakes/category", body);
      return res.data.data as { category: string; categories: string[] };
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to update redesign category",
        );
      }
      return rejectWithValue("Failed to update redesign category");
    }
  },
);

export const fetchRedesignMistakes = createAsyncThunk(
  "mistakeTracker/fetch",
  async (params: MistakeQuery, { rejectWithValue }) => {
    try {
      const res = await api.get("/units/mistakes", { params });
      return res.data.data as Omit<MistakeState, "loading" | "error">;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to load mistakes",
        );
      }
      return rejectWithValue("Failed to load mistakes");
    }
  },
);

const mistakeTrackerSlice = createSlice({
  name: "mistakeTracker",
  initialState,
  reducers: {
    clearMistakeTracker: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRedesignMistakes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRedesignMistakes.fulfilled, (state, action) => {
        state.loading = false;
        state.fromDate = action.payload.fromDate;
        state.toDate = action.payload.toDate;
        state.summary = action.payload.summary;
        state.categoryOptions = action.payload.categoryOptions || [];
        state.peopleOptions = action.payload.peopleOptions || [];
        state.categories = action.payload.categories;
        state.people = action.payload.people;
        state.events = action.payload.events;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.total = action.payload.total;
      })
      .addCase(fetchRedesignMistakes.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to load mistakes";
      });
  },
});

export const { clearMistakeTracker } = mistakeTrackerSlice.actions;
export default mistakeTrackerSlice.reducer;
